import {
  Button,
  Form,
  Input,
  Select,
  Card,
  Typography,
  Space,
  Alert,
  Divider,
  Row,
  Col,
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { rutValidator } from '../../utils/formValidator';
import { useAppApi } from 'hooks/useAppApi';
import {
  UserOutlined,
  MailOutlined,
  IdcardOutlined,
  LockOutlined,
  TeamOutlined,
  SaveOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

function UserForm(props) {
  const {
    submitForm,
    buttonLabel,
    formProps,
    editing = false,
    isSubmitting = false,
    onCancel,
  } = props;
  const [form] = useForm();
  const [error, setError] = useState(null);

  // El catálogo de roles (con su etiqueta legible y si el actor lo puede crear) lo
  // decide la API: el formulario solo lo pinta. Ver
  // docs/superpowers/specs/2026-07-16-catalogo-roles-usuario-design.md
  const identity = useCallback((d) => d, []);
  const {
    data: rolesCatalogo,
    isLoading: rolesLoading,
    error: rolesError,
    call: fetchRoles,
  } = useAppApi({ baseUrl: '/user/roles', mapResults: identity });

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    if (rolesError) {
      setError(
        'No se pudieron cargar los tipos de usuario. Actualiza la página e inténtalo de nuevo.'
      );
    }
  }, [rolesError]);

  const typeOptions = useMemo(() => {
    const catalogo = Array.isArray(rolesCatalogo) ? rolesCatalogo : [];
    // Al crear, no tiene sentido ofrecer un rol que el servidor luego rechazaría.
    if (!editing) {
      return catalogo.filter((r) => r.creable).map(({ value, label }) => ({ value, label }));
    }
    // Al editar, se muestran los cinco roles: el tipo actual del usuario debe verse con
    // su etiqueta legible aunque el actor no pueda crearlo (ej. un ejecutivo editando al
    // super administrador). Los no creables quedan deshabilitados.
    return catalogo.map(({ value, label, creable }) => ({ value, label, disabled: !creable }));
  }, [rolesCatalogo, editing]);

  useEffect(() => {
    form.resetFields();
  }, [formProps, form]);

  const finishForm = async () => {
    setError(null);
    try {
      const values = form.getFieldsValue();
      const response = await submitForm(values, formProps);
      if (response?.resetFields) {
        form.resetFields();
      }
    } catch (err) {
      setError('Error al procesar el formulario. Por favor, inténtalo de nuevo.');
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      form.resetFields();
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header del formulario */}
      <div className="text-center mb-6">
        <Title level={3} className="flex items-center justify-center mb-2">
          <UserOutlined className="mr-2 text-blue-500" />
          {editing ? 'Editar Usuario' : 'Nuevo Usuario'}
        </Title>
        <Text type="secondary">
          {editing
            ? 'Actualiza la información del usuario del sistema'
            : 'Completa la información para crear un nuevo usuario'}
        </Text>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          className="mb-4"
          closable
          onClose={() => setError(null)}
        />
      )}

      <Card className="shadow-md">
        <Form {...formProps} form={form} onFinish={finishForm} layout="vertical" size="large">
          {/* Información Personal */}
          <div className="mb-6">
            <Title level={5} className="flex items-center mb-4">
              <UserOutlined className="mr-2 text-blue-500" />
              Información Personal
            </Title>

            <Form.Item
              name="fullname"
              label={
                <span className="flex items-center">
                  <UserOutlined className="mr-1" />
                  Nombre Completo
                </span>
              }
              rules={[
                {
                  required: true,
                  message: 'El nombre completo es obligatorio',
                },
                {
                  min: 2,
                  message: 'El nombre debe tener al menos 2 caracteres',
                },
                {
                  max: 180,
                  message: 'El nombre no puede tener más de 180 caracteres',
                },
                {
                  pattern: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/,
                  message: 'El nombre solo puede contener letras y espacios',
                },
              ]}
            >
              <Input
                placeholder="Ingresa el nombre completo"
                prefix={<UserOutlined className="text-gray-400" />}
              />
            </Form.Item>

            <Form.Item
              name="rut"
              label={
                <span className="flex items-center">
                  <IdcardOutlined className="mr-1" />
                  RUT
                </span>
              }
              rules={[
                {
                  required: true,
                  message: 'El RUT es obligatorio',
                },
                {
                  max: 12,
                  message: 'El RUT no puede tener más de 12 caracteres',
                },
                {
                  validator: (_, value) =>
                    rutValidator(value)
                      ? Promise.resolve()
                      : Promise.reject(new Error('El RUT ingresado no es válido')),
                },
              ]}
            >
              <Input
                placeholder="20419279-0"
                prefix={<IdcardOutlined className="text-gray-400" />}
              />
            </Form.Item>

            <Form.Item
              name="email"
              label={
                <span className="flex items-center">
                  <MailOutlined className="mr-1" />
                  Correo Electrónico
                </span>
              }
              rules={[
                {
                  required: true,
                  message: 'El correo electrónico es obligatorio',
                },
                {
                  type: 'email',
                  message: 'Debe ingresar un correo electrónico válido',
                },
                {
                  max: 80,
                  message: 'El correo no puede tener más de 80 caracteres',
                },
              ]}
            >
              <Input
                placeholder="correo@ejemplo.com"
                prefix={<MailOutlined className="text-gray-400" />}
              />
            </Form.Item>

            <Form.Item
              name="type"
              label={
                <span className="flex items-center">
                  <TeamOutlined className="mr-1" />
                  Tipo de Usuario
                </span>
              }
              rules={[
                {
                  required: true,
                  message: 'El tipo de usuario es obligatorio',
                },
              ]}
            >
              <Select
                placeholder={rolesLoading ? 'Cargando tipos...' : 'Selecciona el tipo de usuario'}
                size="large"
                loading={rolesLoading}
                disabled={rolesLoading}
                options={typeOptions}
              />
            </Form.Item>
          </div>

          {/* Configuración de Contraseña */}
          {!editing && (
            <>
              <Divider />
              <div className="mb-6">
                <Title level={5} className="flex items-center mb-4">
                  <LockOutlined className="mr-2 text-orange-500" />
                  Configuración de Contraseña
                </Title>

                <Alert
                  message="Información de Seguridad"
                  description={
                    <div>
                      <Text className="block mb-2">
                        <InfoCircleOutlined className="mr-1" />
                        La contraseña debe tener al menos 8 caracteres
                      </Text>
                      <Text type="secondary" className="text-sm">
                        Asegúrate de usar una contraseña segura y fácil de recordar.
                      </Text>
                    </div>
                  }
                  type="info"
                  showIcon
                  className="mb-4"
                />

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="password"
                      label={
                        <span className="flex items-center">
                          <LockOutlined className="mr-1" />
                          Contraseña
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: 'La contraseña es obligatoria',
                        },
                        {
                          // 8, igual que el backend (`z.string().min(8)`): con 6 el
                          // formulario dejaba pasar contraseñas que la API rechazaba.
                          min: 8,
                          message: 'La contraseña debe tener al menos 8 caracteres',
                        },
                        {
                          max: 40,
                          message: 'La contraseña no puede tener más de 40 caracteres',
                        },
                      ]}
                    >
                      <Input.Password
                        placeholder="Ingresa una contraseña segura"
                        prefix={<LockOutlined className="text-gray-400" />}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      name="repeatPassword"
                      label={
                        <span className="flex items-center">
                          <LockOutlined className="mr-1" />
                          Confirmar Contraseña
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: 'La confirmación de contraseña es obligatoria',
                        },
                        {
                          validator: (_, value) =>
                            form.getFieldValue('password') !== value
                              ? Promise.reject(
                                  new Error('Las contraseñas deben ser exactamente iguales')
                                )
                              : Promise.resolve(),
                        },
                      ]}
                    >
                      <Input.Password
                        placeholder="Confirma tu contraseña"
                        prefix={<LockOutlined className="text-gray-400" />}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </>
          )}

          <Divider />

          {/* Botones de Acción */}
          <Form.Item className="mb-0">
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12}>
                <Button
                  block
                  size="large"
                  className="h-12"
                  disabled={isSubmitting}
                  onClick={handleCancel}
                >
                  Cancelar
                </Button>
              </Col>
              <Col xs={24} sm={12}>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                  size="large"
                  icon={<SaveOutlined />}
                  className="h-12"
                >
                  {isSubmitting
                    ? editing
                      ? 'Guardando...'
                      : 'Creando...'
                    : buttonLabel || 'Enviar'}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export default UserForm;
